import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Drawer,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  CircularProgress,
  ListItemButton,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  useTheme,
  Tooltip,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import CancelIcon from "@mui/icons-material/Cancel";
// import NotificationsIcon from '@mui/icons-material/Notifications';
import axiosInstance from "../../axios/axios";
import noNotification from "../../assets/baseVersion/no_notification.svg";
import noNotificationDark from "../../assets/baseVersion/no_notification_dark.png";
import { useNavigate } from "react-router-dom";
// import ListItemButton from '@mui/material/ListItemButton';
// Notification images
import { ArrowBack } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { setEdit, setScanDetails } from "../../pages/onPremises/myScan/MyScanTable_Details_slice";
import { END_POINTS } from "../../shared";

interface NotificationMessagePart {
  mesg: string;
  fullmesg?: string;
  redirection?: boolean;
}

interface NotificationMessage {
  notification_id?: string;
  message: NotificationMessagePart[];
  notification_time: string;
  read_status?: boolean;
  sub_notification?: string[];
  notification_category_code?: string;
  reference?: {
    scan_id?: string;
  };
  // ...other fields as needed
}

interface NotificationSidebarProps {
  open: boolean;
  onClose: () => void;
  themeMode: "light" | "dark";
  fetchNotificationCounts: () => void;
  initialTab?: "alerts" | "notifications";
  openRequest?: number;
}

const muteOptions = [
  { value: 3, label: "Mute Notifications" },
  { value: 2, label: "Mute Alerts" },
  { value: 1, label: "Mute All" },
  { value: 0, label: "None" },
];

export const notificationImageMap: { [key: string]: string } = {
  accepted_risk: "/assets/images/notifications/risk.svg",
  risk_approved: "/assets/images/notifications/risk.svg",
  risk_rejected: "/assets/images/notifications/risk.svg",
  risk_submitted: "/assets/images/notifications/risk.svg",
  risk_request: "/assets/images/notifications/risk.svg",
  risk_acceptance: "/assets/images/notifications/risk.svg",
  modify_accepted_risk: "/assets/images/notifications/risk.svg",
  whitelist_request: "/assets/images/notifications/risk.svg",
  decommission_device: "/assets/images/notifications/asset.svg",
  recommission_device: "/assets/images/notifications/asset.svg",
  modify_device_location: "/assets/images/notifications/asset.svg",
  delete_device: "/assets/images/notifications/asset.svg",
  add_region_data: "/assets/images/notifications/region.svg",
  delete_region_data: "/assets/images/notifications/region.svg",
  modify_region_level: "/assets/images/notifications/region.svg",
  modify_region_name: "/assets/images/notifications/region.svg",
  role_delete: "/assets/images/notifications/role.svg",
  role_modification: "/assets/images/notifications/role.svg",
  activate_user: "/assets/images/notifications/user.svg",
  deactivate_user: "/assets/images/notifications/user.svg",
  invite_user: "/assets/images/notifications/user.svg",
  modify_user_role: "/assets/images/notifications/user.svg",
  master_file_upload: "/assets/images/notifications/scan.svg",
  api_connection_new_device: "/assets/images/notifications/upload.svg",
  file_upload_new_device: "/assets/images/notifications/upload.svg",
  ssh_connection_new_device: "/assets/images/notifications/upload.svg",
  rescan: "/assets/images/notifications/upload.svg",
};

export const defaultNotificationImage =
  "/assets/images/notifications/default.svg";

const NotificationSidebar: React.FC<NotificationSidebarProps> = ({
  open,
  onClose,
  themeMode,
  fetchNotificationCounts,
  initialTab = "alerts",
  openRequest = 0,
}) => {
  const [tab, setTab] = useState<"alerts" | "notifications">("alerts");
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [alertMessages, setAlertMessages] = useState<NotificationMessage[]>([]);
  const [notificationsId, setNotificationsId] = useState<any>(null);
  const [alertsId, setAlertsId] = useState<any>(null);
  const [notificationMessages, setNotificationMessages] = useState<
    NotificationMessage[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [muteValue, setMuteValue] = useState<number>(0);
  const [alertUnreadCount, setAlertUnreadCount] = useState<number>(0);
  const [notificationUnreadCount, setNotificationUnreadCount] =
    useState<number>(0);
  const [userProfile, setUserProfile] = useState<any>({});
  const [alertEventTypeId, setAlertEventTypeId] = useState<any>(null);
  const [notificationEventTypeId, setNotificationEventTypeId] =
    useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const messagesBoxRef = useRef<HTMLDivElement>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationMessage | null>(null);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
   const dispatch = useDispatch()

  useEffect(() => {
    if (openRequest > 0) {
      setTab(initialTab);
      setFilter("all");
      setShowDetails(false);
      setSelectedNotification(null);
    }
  }, [openRequest, initialTab]);

  const handleScroll = useCallback(() => {
    if (!messagesBoxRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesBoxRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setLoadingMore(true);
      fetchData(tab, currentPage + 1, false);
    }
  }, [
    currentPage,
    loadingMore,
    hasMore,
    tab,
    alertMessages.length,
    notificationMessages.length,
  ]);

  // Reset states when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setLoadingMore(false);
    if (tab === "alerts") {
      setAlertMessages([]);
    } else {
      setNotificationMessages([]);
    }
    fetchData(tab, 1, true);
  }, [tab]);

  useEffect(() => {
    const messagesBox = messagesBoxRef.current;
    if (messagesBox) {
      messagesBox.addEventListener("scroll", handleScroll);
      return () => messagesBox.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Fetch counts and initial data
  useEffect(() => {
    if (open) {
      fetchCounts();
      fetchUserProfile();
      fetchScanData();
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchData("alerts", 1, true);
    }
  }, [open, notificationEventTypeId, alertEventTypeId]);

  const fetchCounts = async () => {
    try {
      const res = await axiosInstance.get(
        "/events-notifications/bell-notification-count"
      );
      if (res.data.status === 200) {
        setAlertUnreadCount(res.data.data.ALERT.count);
        setNotificationUnreadCount(res.data.data.NOTIFICATION.count);
        setAlertEventTypeId(res.data.data.ALERT.event_type_id);
        setNotificationEventTypeId(res.data.data.NOTIFICATION.event_type_id);
      }
    } catch (e) {
      // handle error
    }
  };

  const fetchUserProfile = async () => {
    try {
      const res = await axiosInstance.get("/authentication/user-details");
      if (res.data.status === 200) {
        setUserProfile(res.data.data);
        setMuteValue(res.data.data.mute_notification);
      }
    } catch (e) {}
  };

  const fetchData = async (
    type: "alerts" | "notifications",
    page: number,
    reset: boolean,
    filterOverride?: "all" | "unread"
  ) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const effectiveFilter = filterOverride !== undefined ? filterOverride : filter;
      const req = {
        page_no: page,
        content_no: 10,
        notification_type_id:
          type === "alerts" ? alertEventTypeId : notificationEventTypeId,
        all_notifications: effectiveFilter === "all",
        unread_only: effectiveFilter === "unread",
      };
      const res = await axiosInstance.post(
        "/events-notifications/notifications",
        req
      );
      console.log("Notification API Payload (/events-notifications/notifications):", req);
      if (res.data.status === 200 && res.data.data) {
        const newMessages = res.data.data.notification_messages || [];
        const totalItems =
          type === "alerts" ? alertUnreadCount : notificationUnreadCount;
        setAlertsId(res.data.data.notification_count.ALERT.event_type_id);
        setNotificationsId(
          res.data.data.notification_count.NOTIFICATION.event_type_id
        );
        if (type === "alerts") {
          setAlertMessages((prev) =>
            reset ? newMessages : [...prev, ...newMessages]
          );
        } else {
          setNotificationMessages((prev) =>
            reset ? newMessages : [...prev, ...newMessages]
          );
        }

        setCurrentPage(page);
        // Check if we have more items to load based on total count
        const currentLoadedCount =
          (reset
            ? 0
            : type === "alerts"
            ? alertMessages.length
            : notificationMessages.length) + newMessages.length;
        setHasMore(currentLoadedCount < totalItems);
      }
    } catch (e) {
      console.error("Error fetching notifications:", e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    const value = newValue as "alerts" | "notifications";
    setTab(value);
    fetchData(value, 1, true);
    setShowDetails(false)
  };

  const handleMuteChange = async (value: number) => {
    setMuteValue(value);
    try {
      await axiosInstance.get(
        `/authentication/mute-notification?mute_toggel=${value}`
      );
      fetchUserProfile();
    } catch (e) {}
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.get("/events-notifications/read-notification", {
        params: {
          read_all: "1",
          // notification_id: undefined,
          event_type_id: tab === "alerts" ? alertsId : notificationsId,
        },
      });
      fetchData(tab, 1, true);
      fetchCounts();
      await fetchNotificationCounts();
    } catch (e) {}
  };

  const formatTime = (time: string) => {
    const notificationTime = new Date(time);
    const currentTime = new Date();
    if (isNaN(notificationTime.getTime())) {
      return "Invalid Date";
    }
    const diffMs = currentTime.getTime() - notificationTime.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    // If not today, show as '23 Apr'
    if (
      notificationTime.getDate() !== currentTime.getDate() ||
      notificationTime.getMonth() !== currentTime.getMonth() ||
      notificationTime.getFullYear() !== currentTime.getFullYear()
    ) {
      return notificationTime.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
      });
    }
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return "Now";
  };

  const messages = tab === "alerts" ? alertMessages : notificationMessages;
  const unreadCount =
    tab === "alerts" ? alertUnreadCount : notificationUnreadCount;

  const handleNotificationClick = async (notification: NotificationMessage) => {
    console.log("Notification clicked:", notification);
    // 1. Mark as read
    try {
      await axiosInstance.get("/events-notifications/read-notification", {
        params: {
          read_all: "0",
          notification_id: notification.notification_id,
          event_type_id:
            tab === "alerts" ? alertEventTypeId : notificationEventTypeId,
        },
      });
    } catch (e) {}
    // 2. Update state
    if (tab === "alerts") {
      setAlertMessages((prev) =>
        prev.map((msg) =>
          msg.notification_id === notification.notification_id
            ? { ...msg, read_status: true }
            : msg
        )
      );
    } else {
      setNotificationMessages((prev) =>
        prev.map((msg) =>
          msg.notification_id === notification.notification_id
            ? { ...msg, read_status: true }
            : msg
        )
      );
    }
    // 3. Refresh counts
    fetchCounts();
    await fetchNotificationCounts();
    // 4. Show details if sub_notification exists, otherwise navigate
    const code = notification.notification_category_code;
    const isAcceptedRisk =
      acceptedRiskCodes.includes(code || "") ||
      (typeof notification.message === "string" &&
        notification.message.toLowerCase().includes("risk")) ||
      (Array.isArray(notification.message) &&
        notification.message.some((p: any) =>
          p.mesg?.toLowerCase().includes("risk") || p.fullmesg?.toLowerCase().includes("risk")
        ));

    onClose(); // Close sidebar before navigating
    setTimeout(() => {
      if (deviceManagementCodes.includes(code || "")) {
        navigate("/on-premises/assets");
      } else if (regionManagementCodes.includes(code || "")) {
        navigate("/organization/regions");
      } else if (isAcceptedRisk) {
        navigate("/on-premises/acceptedrisk");
      } else if (newFileUpload.includes(code || "")) {
        if (
          notification &&
          notification?.reference &&
          notification?.reference.scan_id
        ) {
          // fetchScanData();
          dispatch(setEdit({ edit: true, scan_uuid: notification?.reference.scan_id }));
          navigate(`/onprem/scan/${notification?.reference.scan_id}`);
        }
      } else if (roleManagement.includes(code || "")) {
        navigate("/organization/roles");
      } else if (userManagement.includes(code || "")) {
        navigate("/organization/user-management");
      } else if (myScans.includes(code || "")) {
        navigate("/on-premises/scans");
      }
    }, 200);
  };

  const fetchScanData = async () => {
    try {
      const response = await axiosInstance.post(END_POINTS.ON_PREM_MY_SCAN, {
        "timezone": "Asia/Calcutta",
        "filter_sched": [],
        "search": ""
      });
      if (response.data?.data) {
         const scanDetails = response.data?.data.map((data: any) => ({
            scan_uuid: data.scan_uuid,
            onprem_posture_id: data.onprem_posture_id || data.scan_summary_id,
            name: data.scanname
        }));

        dispatch(setScanDetails(scanDetails));
        // dispatch(setScanDetails(response?.data?.data));
      }
    } catch (error) {
      // setIsLoading(false)
    }
  };

  const handleBackClick = () => {
    setShowDetails(false);
    setSelectedNotification(null);
  };

  const deviceManagementCodes = [
    "decommission_device",
    "recommission_device",
    "modify_device_location",
    "delete_device",
  ];
  const regionManagementCodes = [
    "add_region_data",
    "delete_region_data",
    "modify_region_level",
    "modify_region_name",
  ];
  const acceptedRiskCodes = [
    "accepted_risk",
    "risk_approved",
    "risk_rejected",
    "risk_submitted",
    "risk_request",
    "risk_acceptance",
    "modify_accepted_risk",
    "whitelist_request",
    "risk",
  ];
  const newFileUpload = [
    "api_connection_new_device",
    "file_upload_new_device",
    "ssh_connection_new_device",
    "rescan",
  ];
  const roleManagement = ["role_delete", "role_modification"];
  const userManagement = [
    "activate_user",
    "deactivate_user",
    "invite_user",
    "modify_user_role",
  ];
  const myScans = ["master_file_upload"];

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: "all" | "unread"
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      fetchData(tab, 1, true, newFilter);
    }
  };

  const handleBellClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBellClose = () => {
    setAnchorEl(null);
  };

  const bellOpen = Boolean(anchorEl);

  const handleMessageClick = async (
    message: NotificationMessagePart,
    notification: NotificationMessage,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    try {
      await axiosInstance.get("/events-notifications/read-notification", {
        params: {
          read_all: "0",
          notification_id: notification.notification_id,
          event_type_id:
            tab === "alerts" ? alertEventTypeId : notificationEventTypeId,
        },
      });
    } catch (e) {}

    if (tab === "alerts") {
      setAlertMessages((prev) =>
        prev.map((msg) =>
          msg.notification_id === notification.notification_id
            ? { ...msg, read_status: true }
            : msg
        )
      );
    } else {
      setNotificationMessages((prev) =>
        prev.map((msg) =>
          msg.notification_id === notification.notification_id
            ? { ...msg, read_status: true }
            : msg
        )
      );
    }
    // 3. Refresh counts
    fetchCounts();
    await fetchNotificationCounts();
    if (
      notification &&
      notification.sub_notification &&
      notification.sub_notification?.length > 0
    ) {
      setSelectedNotification(notification);
      setShowDetails(true);
    } else {
      const code = notification.notification_category_code;
      const isAcceptedRisk =
        acceptedRiskCodes.includes(code || "") ||
        (typeof notification.message === "string" &&
          notification.message.toLowerCase().includes("risk")) ||
        (Array.isArray(notification.message) &&
          notification.message.some((p: any) =>
            p.mesg?.toLowerCase().includes("risk") || p.fullmesg?.toLowerCase().includes("risk")
          ));

      onClose();
      setTimeout(() => {
        if (deviceManagementCodes.includes(code || "")) {
          navigate("/on-premises/assets");
        } else if (regionManagementCodes.includes(code || "")) {
          navigate("/organization/regions");
        } else if (isAcceptedRisk) {
          navigate("/on-premises/acceptedrisk");
        } else if (newFileUpload.includes(code || "")) {
          if (
            notification &&
            notification?.reference &&
            notification?.reference.scan_id
          ) {
            dispatch(setEdit({ edit: true, scan_uuid: notification?.reference.scan_id }));
            navigate(`/onprem/scan/${notification?.reference.scan_id}`);
          }
        } else if (roleManagement.includes(code || "")) {
          navigate("/organization/roles");
        } else if (userManagement.includes(code || "")) {
          navigate("/organization/user-management");
        } else if (myScans.includes(code || "")) {
          navigate("/on-premises/scans");
        }
      }, 200);
    }
  };

  const renderMessage = (
    message: NotificationMessagePart,
    notification: NotificationMessage
  ) => {
    const messageContent = (
      <Box
        component="span"
        sx={{
          color: message.redirection ? "primary.main" : "inherit",
          cursor: message.redirection ? "pointer" : "default",
          "&:hover": {
            textDecoration: message.redirection ? "underline" : "none",
          },
        }}
        {...(message.redirection && {
          onClick: (event: React.MouseEvent<HTMLElement>) =>
            handleMessageClick(message, notification, event),
        })}
        dangerouslySetInnerHTML={{ __html: message.mesg }}
      />
    );

    const hasEllipsis =
      Boolean(message.mesg && (message.mesg.includes("...") || message.mesg.includes("…"))) ||
      Boolean(message.fullmesg && message.fullmesg !== message.mesg);
    const tooltipTitle = (message.fullmesg || message.mesg || "").replace(/<[^>]*>?/gm, "").trim();

    if (hasEllipsis && tooltipTitle) {
      return (
        <Tooltip title={tooltipTitle} arrow placement="top">
          {messageContent}
        </Tooltip>
      );
    }

    return messageContent;

    return null;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1000, pointerEvents: "auto" }}
      ModalProps={{ style: { pointerEvents: "auto" } }}
      PaperProps={{
        sx: {
          height: "calc(88 - 93px)",

          width: 400,
          // borderRadius: 3,
          boxShadow: 6,
          overflow: "hidden",
          backgroundColor: "background.paper",
          // mr: 2,
          // mt: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 400,
          p: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          pointerEvents: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
            }}
          >
            Alerts & Notifications
          </Typography>
          <IconButton onClick={onClose}>
            <CancelIcon sx={{ fontSize: 20, color: "grey" }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #eee",
            px: 2,
            pt: 1,
            pb: 0,
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              flex: 1,
              "& .MuiTab-root": {
                fontSize: "0.875rem",
                textTransform: "none",
                minHeight: "48px",
                py: 1,
              },
            }}
          >
            <Tab label="Alerts" value="alerts" />
            <Tab label="Notifications" value="notifications" />
          </Tabs>
          <IconButton
            onClick={handleBellClick}
            sx={{
              ml: 1,
              color: muteValue !== 0 ? "error.main" : "inherit",
            }}
          >
            {muteValue !== 0 ? <NotificationsOffIcon /> : <NotificationsIcon />}
          </IconButton>
        </Box>
        <Popover
          open={bellOpen}
          anchorEl={anchorEl}
          onClose={handleBellClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{ zIndex: 1000 }}
        >
          <FormControl sx={{ p: 2 }}>
            <RadioGroup
              value={muteValue}
              onChange={(e) => {
                handleMuteChange(Number(e.target.value));
                handleBellClose();
              }}
            >
              <FormControlLabel
                value={3}
                control={<Radio size="small" />}
                label="Mute Notifications"
              />
              <FormControlLabel
                value={2}
                control={<Radio size="small" />}
                label="Mute Alerts"
              />
              <FormControlLabel
                value={1}
                control={<Radio size="small" />}
                label="Mute All"
              />
              <FormControlLabel
                value={0}
                control={<Radio size="small" />}
                label="None"
              />
            </RadioGroup>
          </FormControl>
        </Popover>

        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant={filter === "all" ? "contained" : "outlined"}
              size="small"
              onClick={() =>
                handleFilterChange({} as React.MouseEvent<HTMLElement>, "all")
              }
              sx={{
                textTransform: "none",
                minWidth: "40px",
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "0.8rem",
                boxShadow: "none",
                px: 1.5,
                py: 0.2,
                bgcolor: isDarkMode
                  ? "#fff"
                  : filter === "all"
                  ? "#1a1a1a"
                  : "transparent",
                color: isDarkMode
                  ? "#000"
                  : filter === "all"
                  ? "#fff"
                  : "#1a1a1a",
                border: isDarkMode
                  ? "1px solid #fff"
                  : filter === "all"
                  ? "none"
                  : "1px solid #bfc0cb",
                "&:hover": {
                  bgcolor: isDarkMode
                    ? "#fff"
                    : filter === "all"
                    ? "#444"
                    : "#f5f5f5",
                  color: isDarkMode
                    ? "#000"
                    : filter === "all"
                    ? "#bfc0cb"
                    : "#1a1a1a",
                  border: isDarkMode
                    ? "1px solid #fff"
                    : filter === "all"
                    ? "none"
                    : "1px solid #bfc0cb",
                },
              }}
            >
              All
            </Button>
            <Button
              variant={filter === "unread" ? "contained" : "outlined"}
              size="small"
              onClick={() =>
                handleFilterChange(
                  {} as React.MouseEvent<HTMLElement>,
                  "unread"
                )
              }
              sx={{
                textTransform: "none",
                minWidth: "60px",
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "0.8rem",
                boxShadow: "none",
                px: 1.5,
                py: 0.2,
                bgcolor: isDarkMode
                  ? "#fff"
                  : filter === "unread"
                  ? "#1a1a1a"
                  : "transparent",
                color: isDarkMode
                  ? "#000"
                  : filter === "unread"
                  ? "#fff"
                  : "#1a1a1a",
                border: isDarkMode
                  ? "1px solid #fff"
                  : filter === "unread"
                  ? "none"
                  : "1px solid #bfc0cb",
                "&:hover": {
                  bgcolor: isDarkMode
                    ? "#fff"
                    : filter === "unread"
                    ? "#444"
                    : "#f5f5f5",
                  color: isDarkMode
                    ? "#000"
                    : filter === "unread"
                    ? "#bfc0cb"
                    : "#1a1a1a",
                  border: isDarkMode
                    ? "1px solid #fff"
                    : filter === "unread"
                    ? "none"
                    : "1px solid #bfc0cb",
                },
              }}
            >
              Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
            </Button>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "999px",
              minWidth: "80px",
              boxShadow: "none",
              px: 1.5,
              py: 0.2,
              bgcolor: unreadCount === 0 ? "#bfc0cb" : "primary.main",
              color: unreadCount === 0 ? "#fff" : "#fff",
              "&:hover": {
                bgcolor: unreadCount === 0 ? "#bfc0cb" : "primary.dark",
              },
            }}
          >
            Mark all as read
          </Button>
        </Box>
        {!showDetails && (
          <Box
            ref={messagesBoxRef}
            className="scroll"
            sx={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : messages.length === 0 ? (
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <img
                  src={
                    themeMode === "dark" ? noNotificationDark : noNotification
                  }
                  alt="No notifications"
                  width={120}
                />
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "text.secondary" }}
                >
                  {tab === "alerts"
                    ? "No alerts found."
                    : "No notifications found."}
                </Typography>
              </Box>
            ) : (
              <>
                <List>
                  {messages.map((msg, idx) => (
                    <ListItemButton
                      key={msg.notification_id || idx}
                      alignItems="center"
                      sx={{
                        bgcolor: msg.read_status
                          ? "background.paper"
                          : "rgba(25, 118, 210, 0.08)",
                        mb: 0.2,
                        cursor: "pointer",
                        pointerEvents: "auto",
                        zIndex: 1000,
                        // borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        py: 0.5,
                        minHeight: 60,
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.25)",
                        },
                      }}
                      onClick={() => handleNotificationClick(msg)}
                    >
                      <ListItemAvatar
                        sx={{
                          minWidth: "32px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          src={
                            notificationImageMap[
                              msg.notification_category_code || ""
                            ] || defaultNotificationImage
                          }
                          variant="rounded"
                          sx={{ width: 20, height: 20 }}
                        ></Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              fontSize: "0.95rem",
                              fontWeight: msg.read_status ? 400 : 600,
                              whiteSpace: "normal",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              m: 0,
                              p: 0,
                              lineHeight: 1.2,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "inline",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                              }}
                            >
                              {Array.isArray(msg.message) ? (
                                msg.message.map((part: any, i: number) => {
                                  const hasEllipsis =
                                    Boolean(part.mesg && (part.mesg.includes("...") || part.mesg.includes("…"))) ||
                                    Boolean(part.fullmesg && part.fullmesg !== part.mesg);
                                  const rawTooltip = (part.fullmesg || part.full_mesg || part.mesg || "").replace(/<[^>]*>?/gm, "").trim();
                                  const partElement = (
                                    <Box
                                      component="span"
                                      key={i}
                                      {...(part.redirection && {
                                        onClick: (e: React.MouseEvent<HTMLElement>) => {
                                          e.stopPropagation();
                                          handleMessageClick(part, msg, e);
                                        },
                                      })}
                                      sx={{
                                        color: part.redirection ? "#5B36D6" : "inherit",
                                        cursor: part.redirection ? "pointer" : "inherit",
                                        fontWeight: /<b>.*<\/b>/.test(part.mesg) ? 600 : 400,
                                        textDecoration: part.redirection ? "underline" : "none",
                                        marginRight: "0.2em",
                                        display: "inline",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: part.mesg,
                                      }}
                                    />
                                  );

                                  return hasEllipsis && rawTooltip ? (
                                    <Tooltip
                                      key={i}
                                      title={rawTooltip}
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            fontSize: "12px",
                                            px: 1.2,
                                            py: 0.6,
                                            maxWidth: 380,
                                          },
                                        },
                                      }}
                                    >
                                      {partElement}
                                    </Tooltip>
                                  ) : (
                                    partElement
                                  );
                                })
                              ) : (
                                (() => {
                                  const strMsg = typeof msg.message === "string" ? msg.message : (msg.message as any)?.mesg || "";
                                  const hasEllipsis = strMsg.includes("...") || strMsg.includes("…");
                                  const rawTooltip = strMsg.replace(/<[^>]*>?/gm, "").trim();
                                  const msgElement = (
                                    <Box
                                      component="span"
                                      sx={{ display: "inline" }}
                                    >
                                      {strMsg}
                                    </Box>
                                  );
                                  return hasEllipsis && rawTooltip ? (
                                    <Tooltip
                                      title={rawTooltip}
                                      arrow
                                      placement="top"
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            fontSize: "12px",
                                            px: 1.2,
                                            py: 0.6,
                                            maxWidth: 380,
                                          },
                                        },
                                      }}
                                    >
                                      {msgElement}
                                    </Tooltip>
                                  ) : (
                                    msgElement
                                  );
                                })()
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                ml: "auto",
                                fontSize: "0.8rem",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                pl: 1,
                              }}
                            >
                              {formatTime(msg.notification_time)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
                <br />
                <br />
                {loadingMore && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
        {showDetails && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: 1 }}
            >
              <IconButton onClick={handleBackClick} size="small">
                <ArrowBack />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  gutterBottom={false}
                  dangerouslySetInnerHTML={{
                    __html:
                      selectedNotification &&
                      Array.isArray(selectedNotification.message)
                        ? selectedNotification.message
                            .map((part) => part.fullmesg || part.mesg)
                            .join(" ")
                        : "",
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#3A2ADA",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  pr: 1,
                  pt: 0.5,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                }}
              >
                {formatTime(selectedNotification?.notification_time || "")}
              </Typography>
            </Box>
            <Divider />

            {selectedNotification?.sub_notification && (
              <Box
                className="scroll"
                sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
              >
                <List>
                  {selectedNotification.sub_notification.map(
                  (sub: any, i: any) => (
                    <React.Fragment key={i}>
                    <ListItem>
                      <ListItemText
                      primary={
                        <Typography variant="body2" sx={{fontSize:"16px",fontWeight:"500"}}>{sub.name}</Typography>
                      }
                      />
                    </ListItem>
                    <Divider />
                    </React.Fragment>
                  )
                  )}
                </List>
                 
              </Box>
            )}
          </Box>
        )}
      </Box>
      {/* <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notification Details</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {selectedNotification && Array.isArray(selectedNotification.message)
              ? selectedNotification.message.map(part => part.fullmesg || part.mesg).join(' ')
              : ''}
          </Typography>
          {selectedNotification?.sub_notification && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={700}>Sub Notifications:</Typography>
              <List>
                {selectedNotification.sub_notification.map((sub:any, i:any) => (
                  <ListItem key={i}>
                    <ListItemText primary={sub.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog> */}
    </Drawer>
  );
};

export default NotificationSidebar;
